import { httpClient } from "../../core/api/client";
import {
  ClementinoExpedition,
  FiltresExpeditions,
  CreateExpeditionDTO,
  ChangeStatusDTO,
  PaymentInitiationDTO,
  PaymentSessionResult,
  VoyageEntity,
  TicketClementino,
  AgenceReferentiel,
  VilleReferentiel,
  QuartierReferentiel,
  PaysReferentiel,
  NatureColisReferentiel,
} from "../../models/crm/clementino/clementino-types";

/* -------------------------------------------------------------------------- */
/*                                1. TRACKING                                 */
/* -------------------------------------------------------------------------- */

function extractArray<T = any>(res: any, preferredKey?: string): T[] {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (preferredKey && Array.isArray(res?.[preferredKey])) return res[preferredKey];
  if (preferredKey && Array.isArray(res?.data?.[preferredKey])) return res.data[preferredKey];
  if (Array.isArray(res?.entities)) return res.entities;
  if (Array.isArray(res?.data?.entities)) return res.data.entities;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.expeditions)) return res.data.expeditions;
  if (Array.isArray(res?.expeditions)) return res.expeditions;
  if (Array.isArray(res?.data?.tickets)) return res.data.tickets;
  if (Array.isArray(res?.tickets)) return res.tickets;
  if (Array.isArray(res?.data?.voyages)) return res.data.voyages;
  if (Array.isArray(res?.voyages)) return res.voyages;
  return [];
}

/**
 * Details complets d'une expedition + historique de tracking.
 * Les informations du tracking sont uniquement dans cet endpoint.
 */
export async function getExpeditionShow(numLt: string): Promise<ClementinoExpedition> {
  const res = await httpClient.get(`/clementino/expeditions/${numLt}`);
  const data = res.data?.expedition || res.data?.entity || res.data || res;
  return data as ClementinoExpedition;
}

/**
 * Liste generale des expeditions avec filtres multi-criteres.
 */
export async function listExpeditions(filtres: FiltresExpeditions = {}): Promise<ClementinoExpedition[]> {
  const res = await httpClient.get("/clementino/expeditions", filtres);
  return extractArray<ClementinoExpedition>(res, "entities");
}

export const fetchExpeditions: (...args: any[]) => Promise<any> = listExpeditions;

/**
 * Recherche rapide d'expeditions par terme (nom, telephone, LT).
 */
export async function searchExpeditions(search: string): Promise<ClementinoExpedition[]> {
  const res = await httpClient.get("/clementino/expeditions/search", { search });
  return extractArray<ClementinoExpedition>(res, "entities");
}

/**
 * Ajouter un evenement / Changer le statut d'une expedition.
 */
export async function changeExpeditionStatus(
  expeditionId: number | string,
  payload: ChangeStatusDTO
): Promise<boolean> {
  await httpClient.patch(`/clementino/expeditions/${expeditionId}/status`, payload);
  return true;
}

/**
 * Creer une expedition standard.
 */
export async function createExpedition(payload: CreateExpeditionDTO): Promise<ClementinoExpedition> {
  const res = await httpClient.post("/clementino/expeditions/create", payload);
  const data = res.data?.expedition || res.data?.entity || res.data || res;
  return data as ClementinoExpedition;
}

/**
 * Creer une expedition brute (commande WhatsApp).
 */
export async function createBrutExpedition(payload: {
  telExpediteur: string;
  whatsappUsername: string;
  commandeBrut: string;
}): Promise<any> {
  const res = await httpClient.post("/clementino/expeditions/brut/create", payload);
  return res.data || res;
}

/* -------------------------------------------------------------------------- */
/*                              2. INTERNATIONAL                              */
/* -------------------------------------------------------------------------- */

/**
 * Liste des expeditions internationales (type=intex) avec filtres pays et dates.
 */
export async function listInternationalExpeditions(
  filtres: FiltresExpeditions = {}
): Promise<ClementinoExpedition[]> {
  const res = await httpClient.get("/clementino/expeditions", {
    ...filtres,
    type: "intex",
  });
  return extractArray<ClementinoExpedition>(res, "entities");
}

/**
 * Liste des voyages / campagnes de depart (manifestes fret).
 */
export async function listVoyages(filtres: {
  date_debut?: string;
  date_fin?: string;
  ville_depart?: string | number;
  agence_depart?: string | number;
} = {}): Promise<VoyageEntity[]> {
  const res = await httpClient.get("/clementino/voyages", filtres);
  return extractArray<VoyageEntity>(res, "entities");
}

/**
 * Initier un paiement Mobile Money (Octopay) pour une ou plusieurs expeditions.
 */
export async function initiatePayment(payload: PaymentInitiationDTO): Promise<PaymentSessionResult> {
  const res = await httpClient.post("/clementino/payments/initiate", payload);
  const session = res.data?.paymentSession || res.data || res;
  return session as PaymentSessionResult;
}

/**
 * Verifier le statut d'un paiement Octopay.
 */
export async function getPaymentStatus(reference: string): Promise<any> {
  const res = await httpClient.get(`/clementino/payments/${reference}/status`);
  return res.data || res;
}

/* -------------------------------------------------------------------------- */
/*                           3. LIVRAISON URBAINE                             */
/* -------------------------------------------------------------------------- */

/**
 * Liste des expeditions receptionnees en agence a livrer (status=RECEPTIONNE).
 */
export async function listExpeditionsReceptionnees(
  filtres: FiltresExpeditions = {}
): Promise<ClementinoExpedition[]> {
  const res = await httpClient.get("/clementino/expeditions", {
    ...filtres,
    status: "RECEPTIONNE",
  });
  return extractArray<ClementinoExpedition>(res, "entities");
}

/**
 * Valider une expedition pour livraison avec definition du quartier destination.
 * Si validePourLivraison est false, le quartier peut etre null.
 */
export async function setQuartierDestination(
  expeditionId: number | string,
  quartierId: number | null,
  validePourLivraison: boolean,
  numLt?: string
): Promise<boolean> {
  try {
    // Tentative sur l'endpoint specifique
    await httpClient.post(`/clementino/expeditions/${expeditionId}/set-quartier-destination`, {
      quartierDestination: quartierId,
      validePourLivraison,
    });
    return true;
  } catch (err: any) {
    if (err.status === 404 && numLt) {
      // Fallback sur update expedition
      await httpClient.post("/clementino/expeditions/update", {
        numLt,
        quartierDestination: quartierId,
        validePourLivraison,
      });
      return true;
    }
    throw err;
  }
}

/**
 * Assigner un coursier a une expedition urbaine.
 */
export async function assignCourier(
  expeditionId: number | string,
  payload: {
    coursier_id: number;
    coursier_type?: "internal" | "external";
    commentaire?: string;
    agence_depart?: number;
    point_relai?: string;
  }
): Promise<boolean> {
  await httpClient.post(`/clementino/expeditions/${expeditionId}/assign`, payload);
  return true;
}

/**
 * Reserver une expedition pour collecte/ramassage.
 */
export async function reserveExpedition(
  expeditionId: number | string,
  payload: { localisationGps?: string; commentaire?: string }
): Promise<boolean> {
  await httpClient.post(`/clementino/expeditions/${expeditionId}/reserve`, payload);
  return true;
}

/**
 * Enregistrer une preuve de livraison.
 */
export async function submitPreuveLivraison(payload: any): Promise<boolean> {
  await httpClient.post("/clementino/expeditions/preuve-livraison", payload);
  return true;
}

/* -------------------------------------------------------------------------- */
/*                         4. RECLAMATIONS (TICKETS)                          */
/* -------------------------------------------------------------------------- */

/**
 * Liste des tickets de reclamation Clementino.
 */
export async function listTickets(filtres: {
  date_debut?: string;
  date_fin?: string;
  status?: string;
  type?: string;
  agence_id?: string | number;
  client_id?: string | number;
  num_ticket?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<TicketClementino[]> {
  const res = await httpClient.get("/clementino/tickets", filtres);
  return extractArray<TicketClementino>(res, "entities");
}

/**
 * References de tickets (types, sujets, statuts).
 */
export async function getTicketReferences(): Promise<any> {
  const res = await httpClient.get("/clementino/tickets/references");
  return res.data || res;
}

/**
 * Creer un ticket de reclamation.
 */
export async function createTicket(payload: {
  type: string;
  sujet: string;
  description: string;
  nomPlaignant?: string;
  telPlaignant?: string;
  emailPlaignant?: string;
  numLt?: string;
}): Promise<TicketClementino> {
  const res = await httpClient.post("/clementino/tickets/create", payload);
  return (res.data || res) as TicketClementino;
}

/**
 * Changer le statut d'un ticket.
 */
export async function updateTicketStatus(
  ticketId: number | string,
  status: string,
  note?: string
): Promise<boolean> {
  await httpClient.post(`/clementino/tickets/status/${ticketId}`, { status, note });
  return true;
}

export const changeTicketStatus = updateTicketStatus;

/* -------------------------------------------------------------------------- */
/*                     5. CONFIGURATIONS & REFERENTIELS                       */
/* -------------------------------------------------------------------------- */

export async function fetchAgences(): Promise<AgenceReferentiel[]> {
  const res = await httpClient.get("/clementino/configurations/agences");
  const data = res.data?.entities || res.data || res;
  return Array.isArray(data) ? data : [];
}

export async function fetchVilles(): Promise<VilleReferentiel[]> {
  const res = await httpClient.get("/clementino/villes");
  const data = res.data?.entities || res.data || res;
  return Array.isArray(data) ? data : [];
}

export async function fetchQuartiersByVille(villeId: number | string): Promise<QuartierReferentiel[]> {
  const res = await httpClient.get(`/clementino/quartiers/ville/${villeId}`);
  const data = res.data?.entities || res.data || res;
  return Array.isArray(data) ? data : [];
}

export async function fetchPays(): Promise<PaysReferentiel[]> {
  const res = await httpClient.get("/clementino/pays");
  const data = res.data?.entities || res.data || res;
  return Array.isArray(data) ? data : [];
}

export async function fetchNaturesColis(): Promise<NatureColisReferentiel[]> {
  const res = await httpClient.get("/clementino/configurations/nature-colis");
  const data = res.data?.entities || res.data || res;
  return Array.isArray(data) ? data : [];
}

export async function calculateTarif(params: {
  ville_depart: number;
  ville_destination: number;
  poids: number;
  nature_colis?: number;
  client?: number;
}): Promise<any> {
  const res = await httpClient.get("/clementino/tarifs/calculate", params);
  return res.data || res;
}